export const PER_PAGE = 4;

// Parses MM/DD/YYYY (API format), YYYY-MM-DD, or ISO 8601
export const parseUsageDate = (dateStr) => {
  if (!dateStr) return new Date();
  // MM/DD/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [month, day, year] = dateStr.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(dateStr.indexOf('T') !== -1 ? dateStr : `${dateStr}T00:00:00`);
};

export const formatUsageMonth = (dateStr, abbreviated = false) => {
  const date = parseUsageDate(dateStr);
  const options = abbreviated
    ? { month: 'short', year: 'numeric' }
    : { month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

// Formats "September 01" matching legacy date:'MMMM dd'
export const formatDayMonth = (dateStr) => {
  const date = parseUsageDate(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit' });
};

export const getChartSlice = (months, chartIndex) => {
  const { length } = months;
  const offset = chartIndex * PER_PAGE;
  return months.slice(Math.max(0, length - offset - PER_PAGE), length - offset);
};

export const getMaxChartIndex = months => Math.max(0, Math.ceil(months.length / PER_PAGE) - 1);

// Computes per-bar usage values; caps at allowableUsage for fdo/limited customers in overage
export const buildChartDatasets = (slice) => {
  let maxUsage = 0;
  let maxAllowed = 0;
  const hasOverage = slice.some(({ overageUsed }) => (overageUsed || 0) > 0);

  const usageValues = slice.map(({ policy, homeUsage, allowableUsage }) => {
    const usage = (policy === 'fdo' || policy === 'limited') && homeUsage > allowableUsage
      ? allowableUsage
      : homeUsage;
    maxUsage = Math.max(usage, maxUsage);
    maxAllowed = Math.max(allowableUsage || 0, maxAllowed);
    return usage;
  });

  const overageValues = hasOverage ? slice.map(({ overageUsed }) => overageUsed || 0) : null;
  // undefined = let Chart.js decide; only set a cap when there's no usage data at all
  const yAxisMax = maxUsage ? undefined : maxAllowed;

  return { usageValues, overageValues, yAxisMax };
};

export const isUsageBased = policy => policy === 'limited' || policy === 'fdo';

const LEARN_MORE = { text: 'Learn More', href: 'http://dataplan.xfinity.com' };
const GET_UNLIMITED = { text: 'Get Unlimited Data', href: 'http://dataplan.xfinity.com' };

const formatCurrency = amount => (amount == null ? '' : Number(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

const getUbbCap = flagEnabledFn => (flagEnabledFn('devices.ubbChargeAmount', { defaultValue: false }) ? '$100' : '$200');

export const buildUserMessage = (usageData, flagEnabledFn) => {
  if (!usageData || !usageData.currentMonth) return null;

  const {
    currentMonth,
    policy,
    courtesyAllowed,
    courtesyRemaining,
    courtesyUsed,
    inPaidOverage,
    tierOfService,
  } = usageData;

  const {
    homeUsage, allowableUsage, unitOfMeasure, overageUsed, additionalIncluded,
    additionalCostPerBlock, additionalUnitsPerBlock, overageCharges, currentCreditAmount,
  } = currentMonth;

  const effectivePolicy = currentMonth.policy || policy;
  const ubbCap = getUbbCap(flagEnabledFn);

  if (!isUsageBased(effectivePolicy)) {
    const monthlyUsageState = { homeUsage: homeUsage || 0, unitOfMeasure };
    if (effectivePolicy !== 'unlimited') return { monthlyUsageState };

    const internetEssentials = tierOfService === 'Internet Essentials'
      && flagEnabledFn('devices.removeCancelTextIE', { defaultValue: false });
    return {
      monthlyUsageState,
      additionalMessage: internetEssentials ? {} : {
        content: 'If you choose to cancel Unlimited Data...',
        tooltip: `If you choose to cancel Unlimited Data and exceed a terabyte of usage, you will be charged $10 for each 50 GB of additional data provided, but charges will not exceed ${ubbCap} each month, no matter how much you use.`,
        aside: LEARN_MORE,
      },
    };
  }

  const remaining = Math.max(0, allowableUsage - homeUsage);
  const monthlyUsageState = {
    remaining,
    allowableUsage,
    unitOfMeasure,
    overageUsed,
    additionalIncluded,
  };

  const courtesy = !inPaidOverage && courtesyRemaining > 0
    ? { remaining: courtesyRemaining, allowed: courtesyAllowed } : null;

  let additionalMessage = null;

  if (effectivePolicy === 'fdo') {
    additionalMessage = {
      content: `If you exceed ${allowableUsage}${unitOfMeasure} this month you will be charged ${formatCurrency(additionalCostPerBlock)} for each ${additionalUnitsPerBlock}${unitOfMeasure} of additional data used.`,
      tooltip: `If you do not exceed ${allowableUsage}${unitOfMeasure} you will receive an automatic ${formatCurrency(currentCreditAmount)} credit on your next bill.`,
      aside: { text: 'Learn More about the Flexible-Data Option', href: 'http://dataplan.xfinity.com' },
    };
    if (overageCharges) {
      additionalMessage = {
        ...additionalMessage, status: 'status--danger', content: `You have been charged ${formatCurrency(overageCharges)} for the additional data used.`, tooltip: `This charge will appear on your next bill. Please note you will be charged ${formatCurrency(additionalCostPerBlock)} for each ${additionalUnitsPerBlock}${unitOfMeasure} of additional data used.`,
      };
    }
  }

  if (effectivePolicy === 'limited') {
    const monthOrMonths = courtesyAllowed > 1 ? 'months' : 'month';
    const tooltipPrefix = courtesyAllowed > 1 ? 'After using these' : 'After using';
    const baseTooltip = `${tooltipPrefix} ${courtesyAllowed} courtesy ${monthOrMonths}, if you exceed the plan you will be charged ${formatCurrency(additionalCostPerBlock)} for each ${additionalUnitsPerBlock}${unitOfMeasure} of additional data provided, but charges will not exceed ${ubbCap} each month, no matter how much you use. You can also purchase our Unlimited Data option.`;
    const usageAtMax = courtesyUsed === courtesyAllowed;

    additionalMessage = {
      status: 'status', content: `You have ${courtesyAllowed} courtesy ${monthOrMonths} to exceed ${allowableUsage}${unitOfMeasure} of usage without charge. Need unlimited internet?`, aside: GET_UNLIMITED, tooltip: baseTooltip,
    };

    if (overageCharges) {
      additionalMessage = { ...additionalMessage, content: `You have been charged ${formatCurrency(overageCharges)} for the additional data provided over your data usage plan. Need unlimited internet?`, tooltip: `This charge will appear on your next bill. Please note you will be charged ${formatCurrency(additionalCostPerBlock)} for each ${additionalUnitsPerBlock}${unitOfMeasure} of additional data provided, but charges will not exceed ${ubbCap} each month, no matter how much you use. You can also purchase our Unlimited Data option.` };
    } else if (overageUsed) {
      const prefix = (courtesyUsed > 0 && !usageAtMax) ? `You have just used ${courtesyUsed} of` : 'You have just used your';
      additionalMessage = { ...additionalMessage, content: `${prefix} ${courtesyAllowed} courtesy ${monthOrMonths} in which you can exceed your data usage plan without charge. Need unlimited internet?`, tooltip: `Going forward, if you exceed your data usage plan you will be charged ${formatCurrency(additionalCostPerBlock)} for each ${additionalUnitsPerBlock}${unitOfMeasure} of additional data provided, but charges will not exceed ${ubbCap} each month, no matter how much you use. You can also purchase our Unlimited Data option.` };
    } else if (courtesyUsed) {
      const usedPrefix = usageAtMax ? 'You have used your' : `You have used ${courtesyUsed} of`;
      additionalMessage = { ...additionalMessage, content: `${usedPrefix} ${courtesyAllowed} courtesy ${monthOrMonths} to exceed ${allowableUsage}${unitOfMeasure} of usage without charge. Need unlimited internet?`, tooltip: !courtesyRemaining ? baseTooltip : undefined };
    }

    if (courtesyRemaining === 0) additionalMessage.status = 'status--danger';
    else if (courtesyUsed > 0) additionalMessage.status = 'status--warning';
  }

  return { monthlyUsageState, courtesy, additionalMessage };
};

// NED UBB banner — Northeast division only when enableNED flag is active
export const buildNedUbbContent = (division, flagEnabledFn) => {
  if (!flagEnabledFn('devices.enableNED', { defaultValue: false }) || division !== 'NORTHEAST_DIVISION') return null;
  return {
    heading: 'Xfinity Internet Data Plan',
    description: 'We are delaying implementation of our data plan in our Northeast markets until 2022. We recognize that our data plans were new for our customers in the Northeast, and while only a very small percentage of customers would be affected by the plan, we are providing them with more time to become familiar with their data usage.',
    color: 'minty',
  };
};
